import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import {
  User,
  Mail,
  Star,
  Shield,
  Eye,
  Trash2,
  MessageSquare,
  AlertTriangle,
} from "lucide-react";

export const WorkerCard = ({ 
  worker, 
  onApprove, 
  onViewDetails, 
  onDelete, 
  onSendMessage 
}) => {
  const { t } = useTranslation();
  return (
    <div className="p-4 hover:bg-white/5 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {worker.picture ? (
            <img
              src={worker.picture.startsWith('http') ? worker.picture : `${process.env.REACT_APP_BACKEND_URL}${worker.picture}`}
              alt={worker.name}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-[#23F7DD]/20 flex items-center justify-center">
              <User className="w-6 h-6 text-[#23F7DD]" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <p className="text-white font-medium">{worker.name}</p>
              {worker.profile?.worker_code && (
                <span className="px-2 py-0.5 bg-[#23F7DD]/10 text-[#23F7DD] rounded text-xs font-mono">
                  {worker.profile.worker_code}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm text-[#94A3B8]">
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {worker.email}
              </span>
              {worker.profile?.average_rating > 0 && (
                <span className="flex items-center gap-1">
                  <Star className="w-3 h-3 text-yellow-400" />
                  {worker.profile.average_rating.toFixed(1)}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Status badges */}
          <div className="flex flex-wrap gap-1">
            <span className={`px-2 py-0.5 rounded text-xs ${
              worker.is_approved 
                ? "bg-green-500/20 text-green-400" 
                : "bg-yellow-500/20 text-yellow-400"
            }`}>
              {worker.is_approved ? t('common.approved') : t('common.pending')}
            </span>
            
            {worker.profile?.norwegian_id && (
              <span className={`px-2 py-0.5 rounded text-xs ${
                worker.profile.norwegian_id_verified
                  ? "bg-green-500/20 text-green-400"
                  : "bg-yellow-500/20 text-yellow-400"
              }`}>
                ID {worker.profile.norwegian_id_verified ? "✓" : "?"}
              </span>
            )}
            
            <span className={`px-2 py-0.5 rounded text-xs ${
              worker.profile?.hms_card_status === "verified"
                ? "bg-green-500/20 text-green-400"
                : worker.profile?.hms_card_status === "expired"
                ? "bg-red-500/20 text-red-400"
                : "bg-yellow-500/20 text-yellow-400"
            }`}>
              HMS {worker.profile?.hms_card_status || t('common.pending').toLowerCase()}
            </span>

            {worker.profile?.contract_signed && (
              <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">
                {t('contracts.title')} ✓
              </span>
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(worker)}
            className="border-[#23F7DD]/30 text-[#23F7DD] hover:bg-[#23F7DD]/10"
          >
            <Eye className="w-4 h-4 mr-1" />
            {t('common.details')}
          </Button>

          {!worker.is_approved && (
            <Button
              size="sm"
              onClick={() => onApprove(worker.user_id)}
              className="bg-green-500 text-white hover:bg-green-600"
            >
              {t('common.confirm')}
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(worker)}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const PendingWorkersSection = ({ 
  pendingWorkers, 
  onApprove, 
  onSendMessage 
}) => {
  const { t } = useTranslation();
  if (pendingWorkers.length === 0) return null;

  return (
    <Card className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-yellow-400" />
          <h3 className="text-lg font-semibold text-yellow-400">
            {t('admin.pendingApprovals')} ({pendingWorkers.length})
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {pendingWorkers.slice(0, 6).map((worker) => (
            <div key={worker.user_id} className="p-3 bg-black/30 rounded-lg border border-yellow-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{worker.name}</p>
                    <p className="text-xs text-[#94A3B8]">{worker.profile?.worker_code || t('common.none')}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onSendMessage(worker)}
                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 p-1 h-8 w-8"
                    title={t('workers.sendMessage')}
                  >
                    <MessageSquare className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => onApprove(worker.user_id)}
                    className="bg-green-500 text-white hover:bg-green-600 h-8 px-3"
                  >
                    {t('common.confirm')}
                  </Button>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-[#94A3B8]">
                <Mail className="w-3 h-3" />
                <span className="truncate">{worker.email}</span>
              </div>
            </div>
          ))}
        </div>
        {pendingWorkers.length > 6 && (
          <p className="text-center text-sm text-[#94A3B8] mt-3">
            {t('admin.andMore', { count: pendingWorkers.length - 6 })}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
